<template>
  <div class="about-article-list">
    <!-- 加载状态 -->
    <div class="loading-mask" v-if="loading">
      <span class="loading-text">加载中...</span>
    </div>

    <!-- 列表视图 -->
    <template v-if="viewMode === 'list'">
      <div
        class="article-card"
        v-for="(item, index) in articleList"
        :key="index"
        @click="showDetail(item)"
      >
        <div class="article-title">{{ item.title }}</div>
        <div class="article-desc" v-if="item.description || item.desc">
          {{ item.description || item.desc }}
        </div>
        <div class="article-date">
          {{ item.publishTime || item.createTime || item.date }}
          <span class="hit">&#x1F441; {{ item.hit || 99 }}</span>
        </div>
      </div>

      <div class="empty" v-if="!loading && articleList.length === 0">
        <p class="empty-text">暂无数据</p>
      </div>

      <!-- 分页 -->
      <div class="pagination-wrap" v-if="total > pageSize">
        <span class="pagination-total">共 {{ total }} 条</span>
        <button
          class="pagination-btn"
          :disabled="pageNo <= 1"
          @click="changePage(pageNo - 1)"
        >上一页</button>
        <button
          v-for="p in pageNumbers"
          :key="p"
          class="pagination-num"
          :class="{ active: p === pageNo, ellipsis: p === '...' }"
          @click="changePage(p)"
        >{{ p }}</button>
        <button
          class="pagination-btn"
          :disabled="pageNo >= totalPages"
          @click="changePage(pageNo + 1)"
        >下一页</button>
      </div>
    </template>

    <!-- 详情视图 -->
    <template v-else>
      <div class="detail-toolbar">
        <button type="button" class="detail-back-btn" @click.stop="backToList">
          <span class="back-arrow">&lsaquo;</span>
          <span>返回列表</span>
        </button>
      </div>
      <h1 class="detail-title">{{ currentArticle.title }}</h1>
      <div class="detail-meta">
        <span v-if="currentArticle.source">来源：{{ currentArticle.source }}</span>
        <span>日期：{{ currentArticle.publishTime || currentArticle.createTime || currentArticle.date }}</span>
        <span>&#x1F441; {{ currentArticle.hit || 99 }}</span>
      </div>
      <div class="detail-body" v-html="currentArticle.content || currentArticle.articleContent"></div>
    </template>
  </div>
</template>

<script>
import { contentList, getContent } from '../../api/content'

export default {
  name: 'AboutArticleList',
  props: {
    categoryId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      viewMode: 'list',
      articleList: [],
      currentArticle: {},
      pageNo: 1,
      pageSize: 10,
      total: 0,
      loading: false,
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize)
    },
    pageNumbers() {
      const total = this.totalPages
      if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1)
      }
      const pages = []
      const cur = this.pageNo
      if (cur <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i)
        pages.push('...')
        pages.push(total)
      } else if (cur >= total - 3) {
        pages.push(1)
        pages.push('...')
        for (let i = total - 4; i <= total; i++) pages.push(i)
      } else {
        pages.push(1)
        pages.push('...')
        for (let i = cur - 1; i <= cur + 1; i++) pages.push(i)
        pages.push('...')
        pages.push(total)
      }
      return pages
    },
  },
  watch: {
    categoryId: {
      immediate: true,
      handler() {
        this.pageNo = 1
        this.viewMode = 'list'
        this.currentArticle = {}
        if (this.categoryId) {
          this.fetchList()
        } else {
          this.articleList = []
          this.total = 0
        }
      },
    },
  },
  methods: {
    async fetchList() {
      this.loading = true
      try {
        const res = await contentList({
          categoryId: this.categoryId,
          pageNo: this.pageNo,
          pageSize: this.pageSize,
        })
        const rows = (res.rows || []).map(item => ({
          ...item,
          img: item.litpic ? JSON.parse(item.litpic)[0].url : ''
        }))
        this.articleList = rows
        this.total = res.total || 0
      } catch (e) {
        console.error('获取列表失败：', e)
      } finally {
        this.loading = false
      }
    },
    changePage(page) {
      if (page === '...' || page < 1 || page > this.totalPages) return
      this.pageNo = page
      this.fetchList()
    },
    async showDetail(item) {
      if (!item || !item.id) return
      this.loading = true
      try {
        const data = await getContent({ id: item.id })
        this.currentArticle = data && typeof data === 'object' ? data : item
        this.viewMode = 'detail'
      } catch (e) {
        console.error('获取详情失败：', e)
      } finally {
        this.loading = false
      }
    },
    backToList() {
      this.viewMode = 'list'
      this.currentArticle = {}
    },
  },
}
</script>

<style scoped lang="scss">
.about-article-list {
  position: relative;
  min-height: 200px;
}

.loading-mask {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
}
.loading-text {
  font-size: 13px;
  color: #999;
}

.article-card {
  background: #fff;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  transition: transform 0.15s ease, opacity 0.15s ease;

  &:active {
    transform: scale(0.97);
    opacity: 0.88;
  }
}

.article-title {
  font-size: 14px;
  font-weight: 600;
  color: #222;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.article-desc {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
  margin-top: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
}

.article-date {
  font-size: 11px;
  color: #999;
  margin-top: 6px;

  .hit {
    margin-left: 12px;
  }
}

.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
}

.empty-text {
  font-size: 14px;
  color: #909399;
  margin: 0;
}

.pagination-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 16px;
  gap: 4px;
}

.pagination-total {
  font-size: 12px;
  color: #606266;
  margin-right: 8px;
}

.pagination-btn,
.pagination-num {
  min-width: 32px;
  height: 32px;
  border: 1px solid #DCDFE6;
  border-radius: 4px;
  background: #fff;
  color: #606266;
  font-size: 13px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  transition: all 0.2s;

  &:hover:not(:disabled):not(.active):not(.ellipsis) {
    color: #0464CA;
    border-color: #0464CA;
  }

  &:disabled {
    color: #C0C4CC;
    cursor: not-allowed;
    background: #F5F7FA;
  }
}

.pagination-num.active {
  background: #0464CA;
  border-color: #0464CA;
  color: #fff;
  cursor: default;
}

.pagination-num.ellipsis {
  border-color: transparent;
  background: transparent;
  cursor: default;
}

/* 详情样式 */
.detail-toolbar {
  margin-bottom: 12px;
  text-align: right;
  position: relative;
}

.detail-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-right: 10px;
  font-size: 12px;
  color: #0464CA;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
}

.back-arrow {
  font-size: 14px;
  font-weight: 300;
  line-height: 1;
}

.detail-title {
  font-size: 17px;
  font-weight: 700;
  color: #222;
  line-height: 1.5;
  margin: 0 0 12px;
}

.detail-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(4, 100, 202, 0.15);
  font-size: 11px;
  color: #999;
}

.detail-body {
  font-size: 14px;
  color: #444;
  line-height: 1.8;

  ::v-deep p {
    margin: 0 0 12px;
  }

  ::v-deep img {
    display: block;
    max-width: 100% !important;
    height: auto !important;
    margin: 12px auto;
    border-radius: 8px;
  }
}
</style>
